import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stack, Title, Card, Group, Text, ThemeIcon, UnstyledButton, Badge } from '@mantine/core';
import {
  IconSettings,
  IconUsersGroup,
  IconChevronRight,
  IconInfoCircle,
  IconUserCircle,
  IconPuzzle,
  IconActivity,
} from '@tabler/icons-react';
import { useAccess } from '../access/AccessContext';
import { PageContainer } from '../components/PageContainer';
import { PermissionControlled } from '../components/permissions';
import { PluginRegistry } from '../plugins';

interface MenuItemProps {
  icon: React.ComponentType<any>;
  label: string;
  description?: string;
  to: string;
  color?: string;
  badge?: string;
}

function MenuItem({ icon: Icon, label, description, to, color = 'blue', badge }: MenuItemProps) {
  const navigate = useNavigate();
  
  return (
    <UnstyledButton onClick={() => navigate(to)} style={{ width: '100%' }}>
      <Card withBorder p="md">
        <Group justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size="lg" variant="light" color={color} style={{ flexShrink: 0 }}>
              <Icon size={20} />
            </ThemeIcon>
            <div style={{ minWidth: 0 }}>
              <Group gap="xs">
                <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>{label}</Text>
                {badge && <Badge style={{ fontSize: 'var(--font-size-xs)' }} color={color}>{badge}</Badge>}
              </Group>
              {description && <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{description}</Text>}
            </div>
          </Group>
          <IconChevronRight size={18} color="gray" style={{ flexShrink: 0 }} />
        </Group>
      </Card>
    </UnstyledButton>
  );
}

export function MorePage() {
  const { t } = useTranslation();
  const { isAdmin } = useAccess();

  // Get menu items from all registered plugins
  const pluginMenuItems = PluginRegistry.getMenuItems();
  
  // Get all registered plugins for admin view
  const allPlugins = PluginRegistry.getAll();
  
  // Debug logging - ALWAYS log
  console.log('=== MorePage DEBUG ===');
  console.log('isAdmin:', isAdmin);
  console.log('allPlugins:', allPlugins);
  console.log('allPlugins.length:', allPlugins.length);
  console.log('pluginMenuItems:', pluginMenuItems);

  return (
    <PageContainer>
      <Stack gap="md">
        <Title order={3} style={{ fontSize: 'var(--font-size-lg)' }}>{t('navigation.more')}</Title>

        <Stack gap="xs">
          <MenuItem
            icon={IconSettings}
            label={t('settings.title')}
            description={t('settings.description')}
            to="/settings"
          />

          {isAdmin && (
            <>
              <MenuItem
                icon={IconActivity}
                label="Мониторинг"
                description="Состояние системы и метрики"
                to="/admin/monitoring"
                color="grape"
              />
              <MenuItem
                icon={IconUsersGroup}
                label={t('navigation.groups')}
                description={t('groups.description')}
                to="/groups"
                color="violet"
              />
              <MenuItem
                icon={IconUserCircle}
                label="Типы профилей"
                description="Управление типами профилей"
                to="/admin/profile-types"
                color="teal"
              />
            </>
          )}

          {/* Plugin menu items */}
          {pluginMenuItems.map((item) => {
            if (item.requireAdmin && !isAdmin) return null;
            
            const permPath = `${item.pluginId}.menu.${item.path.split('/').filter(Boolean).join('.')}`;
            
            return (
              <PermissionControlled key={item.path} path={permPath}>
                <MenuItem
                  icon={item.icon}
                  label={item.title}
                  description={item.description}
                  to={item.path}
                  color={item.color}
                />
              </PermissionControlled>
            );
          })}

          <MenuItem
            icon={IconInfoCircle}
            label={t('about.title')}
            description={`${t('about.version')}: 1.0.0`}
            to="/about"
            color="gray"
          />
        </Stack>

        {/* Plugins section for superadmin - always show if plugins exist */}
        {allPlugins.length > 0 && (
          <>
            <Title order={4} mt="md" style={{ fontSize: 'var(--font-size-md)' }}>
              Плагины {!isAdmin && <Badge style={{ fontSize: 'var(--font-size-xs)' }} color="gray">только для админа</Badge>}
            </Title>
            <Stack gap="xs">
              {allPlugins.map((plugin) => (
                <Card key={plugin.id} withBorder p="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <Group wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <ThemeIcon size="md" variant="light" color="indigo" style={{ flexShrink: 0 }}>
                        <IconPuzzle size={16} />
                      </ThemeIcon>
                      <div style={{ minWidth: 0 }}>
                        <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>{plugin.name}</Text>
                        <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{plugin.description}</Text>
                      </div>
                    </Group>
                    <Group gap={4} style={{ flexShrink: 0 }}>
                      <Badge style={{ fontSize: 'var(--font-size-xs)' }} color="green">v{plugin.version}</Badge>
                      <Badge style={{ fontSize: 'var(--font-size-xs)' }} color={plugin.enabled !== false ? 'blue' : 'gray'}>
                        {plugin.enabled !== false ? 'Вкл' : 'Выкл'}
                      </Badge>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </PageContainer>
  );
}

export default MorePage;
